import { useState } from "react";
import apiClient from "../../services/api";
import { toast } from "sonner";
import { KeyRound, Check, X } from "lucide-react";
import { Button } from "./button";
import { Input } from "./input";
import { Label } from "./label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./dialog";
const RequirementItem = ({ met, text }) => (
  <div className={`flex items-center gap-2 text-sm ${met ? "text-green-600" : "text-muted-foreground"}`}>
    {met ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
    {text}
  </div>
);

const PasswordChangeDialog = ({ open, onSuccess, getAuthHeaders, currentPassword }) => {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // Password requirements
  const hasCapital = /[A-Z]/.test(newPassword);
  const hasNumber = /[0-9]/.test(newPassword);
  const hasSymbol = /[!@#$%^&*(),.?":{}|<>]/.test(newPassword);
  const hasMinLength = newPassword.length >= 8;
  const passwordsMatch = newPassword === confirmPassword && confirmPassword.length > 0;
  const isValid = hasCapital && hasNumber && hasSymbol && hasMinLength && passwordsMatch;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid) return;

    setLoading(true);
    try {
      await apiClient.post(`/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      });
      
      toast.success("Password changed successfully!");
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-md" hideCloseButton>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="w-5 h-5" />
            Change Your Password
          </DialogTitle>
          <DialogDescription>
            For security, you must change your password before continuing.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="form-group">
            <Label htmlFor="new-password">New Password</Label>
            <Input
              id="new-password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              autoFocus
              data-testid="new-password-input"
            />
            
            {/* Password Requirements */}
            <div className="mt-3 p-3 bg-muted/50 rounded-lg space-y-1">
              <p className="text-sm font-medium mb-2">Password Requirements:</p>
              <RequirementItem met={hasMinLength} text="At least 8 characters" />
              <RequirementItem met={hasCapital} text="At least 1 capital letter" />
              <RequirementItem met={hasNumber} text="At least 1 number" />
              <RequirementItem met={hasSymbol} text="At least 1 symbol (!@#$%^&*)" />
            </div>
          </div>
          
          <div className="form-group">
            <Label htmlFor="confirm-password">Confirm New Password</Label>
            <Input
              id="confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              data-testid="confirm-password-input"
            />
            {confirmPassword && !passwordsMatch && (
              <p className="text-sm text-destructive mt-1">Passwords do not match</p>
            )}
          </div>

          <Button type="submit" className="w-full" disabled={!isValid || loading} data-testid="change-password-btn">
            {loading ? "Changing Password..." : "Change Password"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export { PasswordChangeDialog };
